import React, { useState, useContext } from 'react';
import { Card, TextField, Button, Tabs, Tab, IconButton, InputAdornment, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { login as loginUser, sendVerificationCode, verifyEmailAndRegister } from '../../services/authService';
import { UserContext } from '../../contexts/UserContext';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import './Login.css';
import { getUserProfile } from '../../services/userService';
import { useTranslation } from 'react-i18next';

const LoginApp = ({ setLoggedIn }) => {
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerFirstname, setRegisterFirstname] = useState('');
  const [registerLastname, setRegisterLastname] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const navigate = useNavigate();
  const { login } = useContext(UserContext);
  const { t } = useTranslation();

  const handleInputChange = (event, setState) => {
    setState(event.target.value);
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    const userData = { login: loginEmail, password: loginPassword };
    try {
      const response = await loginUser(userData);
      const { token, isAdmin, userId } = response;

      const userProfile = await getUserProfile(token);
      const { firstname, email, username, avatar, avatarContentType } = userProfile;
      const user = { firstname, email, username, avatar, avatarContentType, token, isAdmin, userId };

      localStorage.setItem('user', JSON.stringify(user));
      login(user);

      if (user.isAdmin) {
        navigate('/admin-dashboard');
      } else {
        navigate('/dashboard');
      }
      setLoggedIn(true);
    } catch (error) {
      console.error('Login failed', error.response ? error.response.data : "No response");
      toast.error(t('auth.wrongCredentials'));
    }
  };

  const handleSendVerificationCode = async () => {
    if (!registerEmail) {
      toast.error(t('auth.emailRequired'));
      return;
    }
    if (!registerFirstname || !registerLastname) {
      toast.error(t('auth.nameRequired'));
      return;
    }
    if (!registerPassword || !/^(?=.*[A-Z]).{6,}$/.test(registerPassword)) {
      toast.error(t('auth.passwordRequirements'));
      return;
    }
    if (registerPassword !== confirmPassword) {
      toast.error(t('auth.passwordsNotMatch'));
      return;
    }

    const userData = {
      email: registerEmail,
      username: registerUsername,
      firstname: registerFirstname,
      lastname: registerLastname,
      password: registerPassword
    };
    console.log(userData)
    try {
      await sendVerificationCode(userData);
      toast.success(t('auth.verificationCodeSent'));
      setIsVerificationSent(true);
    } catch (error) {
      if (error.response && error.response.status === 409) {
        toast.error(t('auth.emailAlreadyExists'));
      } else {
        console.error('Error sending verification code', error);
        toast.error(t('auth.failedToSendCode'));
      }
    }
  };


  const handleVerificationSubmit = async (event) => {
    event.preventDefault();
    console.log({
      email: registerEmail,
      verificationCode,
      username: registerUsername,
      firstname: registerFirstname,
      lastname: registerLastname,
      password: registerPassword,
  }); // Log the data to confirm

    try {
      const data = await verifyEmailAndRegister(
        registerEmail,
        verificationCode,
        registerUsername,
        registerPassword,
        registerFirstname,
        registerLastname
      );
      toast.success(t('auth.emailVerified'));

      const user = { token: data.token, userId: data.userId };
      localStorage.setItem('user', JSON.stringify(user));
      login(user);

      navigate('/welcome');
      setLoggedIn(true);
    } catch (error) {
      if (error.response && error.response.status === 409 && error.response.data.message.includes('Username')) {
        toast.error(t('auth.usernameExists'));
      } else {
        console.error('Verification failed', error);
        toast.error(t('auth.verificationFailed'));
      }
    }
  };


  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const toggleShowPassword = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  return (
    <>
      <Card className={`login-card ${tabIndex === 0 ? 'login' : 'register'}`}>
        <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth" className="login-tabs">
          <Tab label={t('auth.loginTab')} className="login-tab" />
          <Tab label={t('auth.signupTab')} className="login-tab" />
        </Tabs>
        {tabIndex === 0 && (
          <form className="login-form login" onSubmit={handleLoginSubmit}>
            <TextField
              label={t('auth.email')}
              type="email"
              value={loginEmail}
              onChange={(e) => handleInputChange(e, setLoginEmail)}
              fullWidth
              margin="normal"
              variant="outlined"
              className="login-input"
            />
            <TextField
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              value={loginPassword}
              onChange={(e) => handleInputChange(e, setLoginPassword)}
              fullWidth
              margin="normal"
              variant="outlined"
              className="login-input"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleShowPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="contained" className="login-button">
              {t('auth.loginButton')}
            </Button> <br></br><br></br>
            <Typography variant="body2" align="center" className="forgot-password">
              <Link to="/forgot-password" className="forgot-password-link">
                {t('auth.forgotPassword')}
              </Link>
            </Typography>
          </form>

        )}
        {tabIndex === 1 && (
          <form className="login-form register">
            <TextField
              label={t('auth.email')}
              type="email"
              value={registerEmail}
              onChange={(e) => handleInputChange(e, setRegisterEmail)}
              fullWidth
              margin="normal"
              variant="outlined"
              className="login-input"
              required
            />
            <TextField
              label={t('auth.username')}
              value={registerUsername}
              onChange={(e) => handleInputChange(e, setRegisterUsername)}
              fullWidth
              margin="normal"
              variant="outlined"
              className="login-input"
            />
            <TextField
              label={t('auth.firstname')}
              value={registerFirstname}
              onChange={(e) => handleInputChange(e, setRegisterFirstname)}
              fullWidth
              margin="normal"
              variant="outlined"
              className="login-input"
              required
            />
            <TextField
              label={t('auth.lastname')}
              value={registerLastname}
              onChange={(e) => handleInputChange(e, setRegisterLastname)}
              fullWidth
              margin="normal"
              variant="outlined"
              className="login-input"
              required
            />
            <TextField
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              value={registerPassword}
              onChange={(e) => handleInputChange(e, setRegisterPassword)}
              fullWidth
              margin="normal"
              variant="outlined"
              className="login-input"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleShowPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('auth.confirmPassword')}
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => handleInputChange(e, setConfirmPassword)}
              fullWidth
              margin="normal"
              variant="outlined"
              className="login-input"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleShowPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                label={t('auth.verificationCode')}
                type="text"
                value={verificationCode}
                onChange={(e) => handleInputChange(e, setVerificationCode)}
                variant="outlined"
                className="login-input"
                style={{ flex: 2, marginRight: '145px' }} // Make the input field larger
              />
              <Button
                variant="contained"
                onClick={handleSendVerificationCode}
                style={{ minWidth: '80px', padding: '6px 8px' }}
              >
                {t('auth.sendCodeButton')}
              </Button>
            </div>
            <Button
              variant="contained"
              onClick={handleVerificationSubmit}
              className="login-button"
              style={{ marginTop: '20px' }}
            >
              {t('auth.registerButton')}
            </Button>
          </form>
        )}

      </Card>
    </>
  );
};

export default LoginApp;
